import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { AlertTriangle, ArrowUp, Check, FileText, Headset, Loader2, MessageCircle, Mic, Package, Paperclip, Plus, Settings2, Sparkles, Square, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { isApiError } from '@/lib/api/errors';
import { http } from '@/lib/api/http';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/button';

interface Step { key?: string; method: 'POST' | 'PUT'; path: string; body: Record<string, unknown> }
type Action =
  | { type: 'OPEN_PAGE'; path: string; label: string }
  | { type: 'API'; summary: string; steps: Step[]; open?: string };
interface Attachment { name: string; mimeType: string; size: number }
interface Msg { id: string; role: 'USER' | 'ASSISTANT' | 'OPERATOR' | 'SYSTEM'; content: string; action: Action | null; actionState: 'PROPOSED' | 'DONE' | 'DISMISSED' | null; attachment?: Attachment | null; feedback?: 1 | -1 | null; createdAt: string }
interface Conv { id: string; title: string | null; status: 'BOT' | 'HUMAN' | 'CLOSED'; unreadForUser: boolean }
interface State { configured?: boolean; conversation: Conv | null; messages: Msg[] }
interface Insight { kind: 'overdue' | 'oldDrafts' | 'lowStock' | 'setup'; count: number; amount?: string }

/** The only endpoints a confirmed proposal may call (the server proposes nothing else; this is the second lock). */
const U = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const ALLOWED: Array<[string, RegExp]> = [
  ['POST', /^\/sales\/documents$/], ['PUT', new RegExp(`^/sales/documents/${U}$`)],
  ['POST', new RegExp(`^/sales/documents/${U}/(convert|duplicate|validate)$`)], ['POST', new RegExp(`^/sales/invoices/${U}/credit-notes$`)],
  ['POST', /^\/parties$/], ['POST', /^\/products$/], ['PUT', /^\/onboarding\/profile$/], ['POST', /^\/payments$/],
  ['POST', /^\/purchasing\/orders$/], ['POST', /^\/documents\/render$/], ['POST', new RegExp(`^/documents/${U}/send$`)],
];
const SUGGESTIONS = ['invoice', 'customer', 'unpaid', 'setup', 'human'] as const;
const INSIGHT_ICON = { overdue: AlertTriangle, oldDrafts: FileText, lowStock: Package, setup: Settings2 };
const FILE_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_FILE = 5 * 1024 * 1024;

/** Messages stored before multi-step actions had { method, path, body }. */
const stepsOf = (a: Extract<Action, { type: 'API' }> & Partial<Step>): Step[] => a.steps ?? [{ method: a.method!, path: a.path!, body: a.body ?? {} }];

/** Light formatting of the assistant's text: **bold**, numbered and bulleted lines. Built as React nodes, never HTML. */
function RichText({ text }: { text: string }) {
  const inline = (s: string) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>));
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => { if (list) { const L = list.ordered ? 'ol' : 'ul'; blocks.push(<L key={blocks.length} className={cn('space-y-1 ps-5', list.ordered ? 'list-decimal' : 'list-disc')}>{list.items.map((it, i) => <li key={i}>{inline(it)}</li>)}</L>); list = null; } };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) { flush(); continue; }
    const h = /^#{1,6}\s+(.*)$/.exec(line);
    if (h) { flush(); blocks.push(<p key={blocks.length} className="font-semibold">{inline((h[1] ?? '').replace(/\*\*/g, ''))}</p>); continue; }
    const m = /^(\d+[.)]|[-*•])\s+(.*)$/.exec(line);
    if (m) { const ordered = /\d/.test(m[1] ?? ''); if (!list || list.ordered !== ordered) { flush(); list = { ordered, items: [] }; } list.items.push(m[2] ?? ''); continue; }
    flush();
    if (line) blocks.push(<p key={blocks.length}>{inline(line)}</p>);
  }
  flush();
  return <div className="space-y-2">{blocks}</div>;
}

export function AssistantWidget() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const page = useRouterState({ select: (r) => r.location.pathname });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [file, setFile] = useState<{ id: string; name: string; mimeType: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const status = useQuery({ queryKey: ['assistant'], queryFn: () => http.get<State>('/assistant/status'), staleTime: 15_000,
    refetchInterval: (q) => (q.state.data?.conversation?.status === 'HUMAN' ? (open ? 5_000 : 30_000) : false) });
  const data = status.data;
  const conv = data?.conversation ?? null;
  const messages = data?.messages ?? [];
  const insights = useQuery({ queryKey: ['assistant', 'insights'], queryFn: () => http.get<Insight[]>('/assistant/insights'), enabled: open && messages.length === 0, staleTime: 60_000 });
  const set = (s: State) => qc.setQueryData<State>(['assistant'], (old) => ({ ...old, ...s, conversation: s.conversation?.status === 'CLOSED' ? null : s.conversation }));
  const setMsgs = (msgs: Msg[]) => qc.setQueryData<State>(['assistant'], (old) => ({ ...old!, messages: msgs }));
  const fail = (e: unknown) => toast.error(isApiError(e) ? e.message : t('errors.INTERNAL_ERROR'));

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [open, messages.length]);
  useEffect(() => { if (open && conv?.unreadForUser) void http.get<State>(`/assistant/conversations/${conv.id}`).then(set); }, [open, conv?.unreadForUser]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => recorder.current?.stream.getTracks().forEach((tr) => tr.stop()), []);

  const send = useMutation({
    mutationFn: (v: { text: string; attachmentId?: string; attachment?: Attachment }) =>
      http.post<State>('/assistant/messages', { text: v.text, locale: i18n.language, page, ...(v.attachmentId ? { attachmentId: v.attachmentId } : {}), ...(conv ? { conversationId: conv.id } : {}) }),
    onMutate: (v) => qc.setQueryData<State>(['assistant'], (old) => ({ ...(old ?? { conversation: null, messages: [] }), messages: [...(old?.messages ?? []), { id: `tmp-${Date.now()}`, role: 'USER', content: v.text, attachment: v.attachment ?? null, action: null, actionState: null, createdAt: new Date().toISOString() }] })),
    onSuccess: set,
    onError: (e) => { fail(e); void status.refetch(); },
  });
  const human = useMutation({
    mutationFn: async () => {
      if (!conv) { const r = await http.post<State>('/assistant/messages', { text: t('assistant.humanRequest'), locale: i18n.language, page }); return http.post<State>(`/assistant/conversations/${r.conversation!.id}/human`, { reason: t('assistant.humanRequest') }); }
      return http.post<State>(`/assistant/conversations/${conv.id}/human`, {});
    },
    onSuccess: set,
    onError: fail,
  });
  const restart = async () => { if (conv) await http.post(`/assistant/conversations/${conv.id}/close`).catch(() => undefined); qc.setQueryData<State>(['assistant'], (old) => ({ ...old, conversation: null, messages: [] })); };

  /** Upload one file (photo, PDF or voice note); it goes with the next message. */
  async function upload(blob: Blob, name: string, mimeType: string, sendNow = false) {
    if (blob.size > MAX_FILE) { toast.error(t('assistant.fileTooBig')); return; }
    setUploading(true);
    try {
      const r = await http.upload<{ id: string; name: string; mimeType: string; size: number }>('/assistant/attachments', { name, mimeType }, blob);
      if (sendNow) { send.mutate({ text: text.trim(), attachmentId: r.id, attachment: r }); setText(''); } else setFile(r);
    } catch (e) { fail(e); } finally { setUploading(false); }
  }
  async function toggleRecord() {
    if (recording) { recorder.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ['audio/webm', 'audio/ogg', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
      const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop()); setRecording(false);
        const mime = (rec.mimeType || type || 'audio/webm').split(';')[0]!;
        const blob = new Blob(chunks, { type: mime });
        if (blob.size > 0) void upload(blob, `${t('assistant.voiceNote')}.${mime.split('/')[1]}`, mime, true);
      };
      recorder.current = rec; rec.start(); setRecording(true);
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, 120_000);   // at most 2 minutes
    } catch { toast.error(t('assistant.micDenied')); }
  }

  /** Confirmed by the user: the app runs the allowlisted calls in order with the user's own session, then records the outcome. */
  async function confirm(m: Msg) {
    const a = m.action!;
    setBusyAction(m.id);
    try {
      if (a.type === 'OPEN_PAGE') {
        setMsgs(await http.post<Msg[]>(`/assistant/messages/${m.id}/action`, { state: 'DONE' }));
        await navigate({ to: a.path as never });
      } else {
        const ids: Record<string, string> = {};
        let last: { id?: string } | null = null;
        const steps = stepsOf(a);
        for (const [i, st] of steps.entries()) {
          const path = st.path.replace(/\$ref:([a-z]+)/g, (_, k: string) => ids[k] ?? '');
          if (!ALLOWED.some(([method, re]) => method === st.method && re.test(path))) throw new Error('Action not allowed');
          const body = JSON.parse(JSON.stringify(st.body), (_k, v) => (typeof v === 'string' && v.startsWith('$ref:') ? ids[v.slice(5)] : v)) as Record<string, unknown>;
          last = st.method === 'POST' ? await http.post<{ id?: string }>(path, body, { idempotencyKey: `assistant-${m.id}-${i}` }) : await http.put<{ id?: string }>(path, body);
          if (st.key && last?.id) ids[st.key] = last.id;
        }
        setMsgs(await http.post<Msg[]>(`/assistant/messages/${m.id}/action`, { state: 'DONE', ...(last?.id ? { resultId: last.id } : {}) }));
        toast.success(t('assistant.done'));
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'assistant' });
        const target = a.open?.replace('{id}', last?.id ?? '');
        // the page on screen holds the old version (an editor would then save over the change): reload it
        if (target === page) window.location.reload();
        else if (target) await navigate({ to: target as never });
      }
    } catch (e) { fail(e); } finally { setBusyAction(null); }
  }
  async function dismiss(m: Msg) {
    const msgs = await http.post<Msg[]>(`/assistant/messages/${m.id}/action`, { state: 'DISMISSED' }).catch(() => null);
    if (msgs) setMsgs(msgs);
  }
  async function rate(m: Msg, v: 1 | -1) {
    const msgs = await http.post<Msg[]>(`/assistant/messages/${m.id}/feedback`, { value: m.feedback === v ? 0 : v }).catch(() => null);
    if (msgs) setMsgs(msgs);
  }
  const submit = () => {
    const v = text.trim();
    if ((!v && !file) || send.isPending || uploading) return;
    setText('');
    send.mutate({ text: v, ...(file ? { attachmentId: file.id, attachment: { name: file.name, mimeType: file.mimeType, size: 0 } } : {}) });
    setFile(null);
  };
  const ask = (q: string) => send.mutate({ text: q });
  const isHuman = conv?.status === 'HUMAN';
  const amount = (v?: string) => Number(v ?? 0).toLocaleString(i18n.language === 'ar' ? 'ar-TN' : 'fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} aria-label={t('assistant.open')}
          className="fixed bottom-20 end-4 z-40 grid size-14 place-items-center rounded-full bg-accent text-accent-fg shadow-lg transition-transform hover:scale-105 lg:bottom-6 lg:end-6">
          <MessageCircle className="size-6" aria-hidden />
          {conv?.unreadForUser && <span className="absolute end-1 top-1 size-3 rounded-full bg-danger ring-2 ring-surface" aria-hidden />}
        </button>
      )}
      {open && (
        <section role="dialog" aria-label={t('assistant.title')}
          className="animate-in fixed inset-x-2 bottom-2 top-16 z-50 flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-lg sm:inset-x-auto sm:end-6 sm:top-auto sm:h-[660px] sm:max-h-[calc(100dvh-6rem)] sm:w-[420px] lg:bottom-6">
          <header className="flex items-center gap-3 bg-nav px-4 py-3 text-white">
            <span className="grid size-9 place-items-center rounded-full bg-white/15">{isHuman ? <Headset className="size-5" aria-hidden /> : <Sparkles className="size-5" aria-hidden />}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{isHuman ? t('assistant.humanTitle') : t('assistant.title')}</p>
              <p className="truncate text-xs text-nav-fg">{isHuman ? t('assistant.humanStatus') : t('assistant.status')}</p>
            </div>
            {!isHuman && <button type="button" onClick={() => human.mutate()} disabled={human.isPending} className="rounded-md p-1.5 hover:bg-white/10" title={t('assistant.talkHuman')} aria-label={t('assistant.talkHuman')}><Headset className="size-4" aria-hidden /></button>}
            <button type="button" onClick={() => void restart()} className="rounded-md p-1.5 hover:bg-white/10" title={t('assistant.newConversation')} aria-label={t('assistant.newConversation')}><Plus className="size-4" aria-hidden /></button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-md p-1.5 hover:bg-white/10" aria-label={t('assistant.close')}><X className="size-4" aria-hidden /></button>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto bg-bg px-4 py-4" aria-live="polite">
            {messages.length === 0 && (
              <div className="space-y-4 py-2 text-center">
                <span className="mx-auto grid size-12 place-items-center rounded-full bg-accent-soft text-accent"><Sparkles className="size-6" aria-hidden /></span>
                <div><p className="font-semibold text-fg">{t('assistant.welcomeTitle')}</p><p className="mt-1 text-sm text-muted">{t('assistant.welcomeText')}</p></div>
                {(insights.data ?? []).length > 0 && (
                  <div className="space-y-2 text-start">
                    <p className="text-xs font-medium text-muted">{t('assistant.insightsTitle')}</p>
                    {insights.data!.map((x) => {
                      const Icon = INSIGHT_ICON[x.kind];
                      return (
                        <button key={x.kind} type="button" onClick={() => ask(t(`assistant.insight.${x.kind}.ask`))}
                          className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface p-3 text-start text-sm hover:border-accent">
                          <span className={cn('grid size-8 shrink-0 place-items-center rounded-full', x.kind === 'overdue' ? 'bg-danger-soft text-danger' : x.kind === 'lowStock' ? 'bg-warning-soft text-warning' : 'bg-accent-soft text-accent')}><Icon className="size-4" aria-hidden /></span>
                          <span className="min-w-0 flex-1 text-fg">{t(`assistant.insight.${x.kind}.label`, { count: x.count, amount: amount(x.amount) })}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => (s === 'human' ? human.mutate() : ask(t(`assistant.suggest.${s}.ask`)))}
                      className="rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent">{t(`assistant.suggest.${s}.label`)}</button>
                  ))}
                </div>
                <p className="text-xs text-muted">{t('assistant.languages')}</p>
              </div>
            )}
            {messages.map((m) => {
              if (m.role === 'SYSTEM') return <p key={m.id} className="mx-auto max-w-[85%] rounded-full bg-surface-3 px-3 py-1 text-center text-xs text-muted">{t(`assistant.sys.${m.content}`, { defaultValue: '' })}</p>;
              const mine = m.role === 'USER';
              const a = m.action;
              return (
                <div key={m.id} className={cn('group flex flex-col gap-1', mine ? 'items-end' : 'items-start')}>
                  {m.role === 'OPERATOR' && <span className="flex items-center gap-1 text-xs font-medium text-info"><Headset className="size-3" aria-hidden />{t('assistant.agent')}</span>}
                  {mine && m.attachment && (
                    <span className="flex max-w-[88%] items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-fg">
                      {m.attachment.mimeType.startsWith('audio/') ? <Mic className="size-3.5 text-accent" aria-hidden /> : <Paperclip className="size-3.5 text-accent" aria-hidden />}
                      <span className="truncate">{m.attachment.mimeType.startsWith('audio/') ? t('assistant.voiceNote') : m.attachment.name}</span>
                    </span>
                  )}
                  {(m.content && !(a && a.type === 'API')) && (
                    <div className={cn('max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed', mine ? 'rounded-ee-sm bg-accent text-accent-fg' : m.role === 'OPERATOR' ? 'rounded-es-sm border border-info-border bg-info-soft text-fg' : 'rounded-es-sm border border-border bg-surface text-fg')}>
                      {mine ? <p className="whitespace-pre-wrap" dir="auto">{m.content}</p> : <div dir="auto"><RichText text={m.content} /></div>}
                    </div>
                  )}
                  {a && (
                    <div className="w-[88%] rounded-xl border border-border bg-surface p-3 text-sm shadow-sm">
                      <p className="mb-1 text-xs font-medium text-muted">{a.type === 'OPEN_PAGE' ? t('assistant.page') : t('assistant.proposal')}</p>
                      <p className="font-medium text-fg" dir="auto">{a.type === 'OPEN_PAGE' ? a.label : a.summary}</p>
                      {a.type === 'API' && stepsOf(a).length > 1 && <p className="mt-1 text-xs text-muted">{t('assistant.steps', { count: stepsOf(a).length })}</p>}
                      {m.actionState === 'PROPOSED' ? (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" variant="primary" loading={busyAction === m.id} onClick={() => void confirm(m)}><Check className="size-4" aria-hidden />{a.type === 'OPEN_PAGE' ? t('assistant.openPage') : t('assistant.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => void dismiss(m)}>{t('assistant.dismiss')}</Button>
                        </div>
                      ) : <p className={cn('mt-2 text-xs font-medium', m.actionState === 'DONE' ? 'text-success' : 'text-muted')}>{t(m.actionState === 'DONE' ? 'assistant.actionDone' : 'assistant.actionDismissed')}</p>}
                    </div>
                  )}
                  {m.role === 'ASSISTANT' && !m.id.startsWith('tmp-') && (
                    <div className={cn('flex gap-0.5 transition-opacity', m.feedback ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100')}>
                      <button type="button" onClick={() => void rate(m, 1)} aria-label={t('assistant.helpful')} title={t('assistant.helpful')} className={cn('rounded p-1 hover:bg-surface-2', m.feedback === 1 ? 'text-success' : 'text-muted')}><ThumbsUp className="size-3.5" aria-hidden /></button>
                      <button type="button" onClick={() => void rate(m, -1)} aria-label={t('assistant.notHelpful')} title={t('assistant.notHelpful')} className={cn('rounded p-1 hover:bg-surface-2', m.feedback === -1 ? 'text-danger' : 'text-muted')}><ThumbsDown className="size-3.5" aria-hidden /></button>
                    </div>
                  )}
                </div>
              );
            })}
            {send.isPending && <div className="flex items-center gap-2 text-sm text-muted"><Loader2 className="size-4 animate-spin" aria-hidden />{isHuman ? t('assistant.sending') : t('assistant.thinking')}</div>}
            <div ref={endRef} />
          </div>

          <form className="border-t border-border bg-surface p-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            {file && (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-surface-2 px-2.5 py-1.5 text-xs text-fg">
                <Paperclip className="size-3.5 text-accent" aria-hidden /><span className="min-w-0 flex-1 truncate">{file.name}</span>
                <button type="button" onClick={() => setFile(null)} aria-label={t('assistant.removeFile')} className="rounded p-0.5 hover:bg-surface-3"><X className="size-3.5" aria-hidden /></button>
              </div>
            )}
            <div className="flex items-end gap-1.5 rounded-xl border border-border bg-surface-2 px-2 py-2 focus-within:border-accent">
              {!isHuman && (
                <>
                  <input ref={fileInput} type="file" accept={FILE_TYPES} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f, f.name, f.type); }} />
                  <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading || recording} title={t('assistant.attach')} aria-label={t('assistant.attach')}
                    className="grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-surface-3 hover:text-fg disabled:opacity-40">{uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Paperclip className="size-4" aria-hidden />}</button>
                </>
              )}
              <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 2000))} rows={1} dir="auto"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder={recording ? t('assistant.recording') : isHuman ? t('assistant.placeholderHuman') : t('assistant.placeholder')} aria-label={t('assistant.placeholder')}
                className="max-h-32 min-h-[32px] flex-1 resize-none bg-transparent py-1.5 text-sm text-fg outline-none placeholder:text-muted" />
              {!isHuman && !text.trim() && !file ? (
                <button type="button" onClick={() => void toggleRecord()} disabled={uploading || send.isPending} aria-label={recording ? t('assistant.stopRecording') : t('assistant.record')} title={recording ? t('assistant.stopRecording') : t('assistant.record')}
                  className={cn('grid size-8 shrink-0 place-items-center rounded-lg disabled:opacity-40', recording ? 'animate-pulse bg-danger text-white' : 'bg-accent text-accent-fg')}>
                  {recording ? <Square className="size-3.5" aria-hidden /> : <Mic className="size-4" aria-hidden />}
                </button>
              ) : (
                <button type="submit" disabled={(!text.trim() && !file) || send.isPending || uploading} aria-label={t('assistant.send')}
                  className="grid size-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-fg disabled:opacity-40"><ArrowUp className="size-4" aria-hidden /></button>
              )}
            </div>
            <p className="mt-1.5 text-center text-[11px] text-muted">{t('assistant.disclaimer')}</p>
          </form>
        </section>
      )}
    </>
  );
}
